import { Link as LinkIcon, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useChatbot } from '../../../contexts/ChatbotContext';
import { useQuickActions } from '../../../hooks/useQuickActions';
import type { QuickAction } from '../../../types/quickActions';

export function QuickActionsSettings() {
  const { chatbotPublicId } = useChatbot();
  const { actions, loading, error, setQuickActions, initializeQuickActions } = useQuickActions();
  const [localActions, setLocalActions] = useState<QuickAction[]>([]);

  // Initialize quick actions and local state
  useEffect(() => {
    if (chatbotPublicId) {
      initializeQuickActions(chatbotPublicId);
    }
  }, [chatbotPublicId, initializeQuickActions]);

  // Update local state when actions change
  useEffect(() => {
    setLocalActions(actions);
  }, [actions]);

  const handleActionChange = (index: number, field: 'text' | 'url', value: string) => {
    const newActions = localActions.map((action, i) => 
      i === index ? { ...action, [field]: value } : action
    );
    setLocalActions(newActions);
  };

  const handleActionBlur = async (index: number) => {
    if (!chatbotPublicId) return;
    await setQuickActions(localActions, chatbotPublicId);
  };

  const handleAddAction = async () => {
    if (!chatbotPublicId) return;
    const newActions = [...localActions, { text: 'New action' }];
    setLocalActions(newActions);
    await setQuickActions(newActions, chatbotPublicId);
  };

  const handleRemoveAction = async (index: number) => {
    if (!chatbotPublicId) return;
    const newActions = localActions.filter((_, i) => i !== index);
    setLocalActions(newActions);
    await setQuickActions(newActions, chatbotPublicId);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <label className="block text-sm font-medium text-gray-300">
          Customizable quick actions
        </label>
        <button 
          onClick={handleAddAction}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-1 rounded-lg bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="w-4 h-4" />
          {loading ? 'Loading...' : 'Add'}
        </button>
      </div>

      {error && (
        <div className="text-red-400 text-sm">
          Error: {error.message}
        </div>
      )}

      <div className="space-y-3">
        {localActions.map((action, index) => (
          <div key={index} className="bg-white/5 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-3">
              <input
                type="text"
                value={action.text}
                onChange={(e) => handleActionChange(index, 'text', e.target.value)}
                onBlur={() => handleActionBlur(index)}
                placeholder="Action text"
                className="flex-1 bg-transparent border-none text-white focus:outline-none text-sm leading-relaxed"
                disabled={loading}
                style={{ lineHeight: '1.5' }}
              />
              <button 
                onClick={() => handleRemoveAction(index)}
                className="text-red-400 hover:text-red-300 transition-colors disabled:opacity-50"
                disabled={loading}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            
            <div className="flex items-center gap-2">
              <LinkIcon className="w-4 h-4 text-gray-400" />
              <input
                type="url"
                value={action.url || ''}
                onChange={(e) => handleActionChange(index, 'url', e.target.value)}
                onBlur={() => handleActionBlur(index)}
                placeholder="URL (optional)"
                className="flex-1 bg-transparent border-none text-white focus:outline-none text-sm leading-relaxed"
                disabled={loading}
                style={{ lineHeight: '1.5' }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}